export interface GoogleMapsError extends Error {
  type:
    | "DISABLED"
    | "API_ERROR"
    | "NETWORK_ERROR"
    | "INVALID_RESPONSE"
    | "QUOTA_EXCEEDED"
    | "UNAUTHORIZED";
  status?: number;
  details?: string;
  canRetry?: boolean;
  shouldFallback?: boolean;
}

export class GoogleMapsDisabledError extends Error implements GoogleMapsError {
  readonly type = "DISABLED" as const;
  status = 503;
  canRetry = false;
  shouldFallback = true;

  constructor(message = "Google Maps services are currently disabled") {
    super(message);
    this.name = "GoogleMapsDisabledError";
  }
}

export class GoogleMapsAPIError extends Error implements GoogleMapsError {
  readonly type = "API_ERROR" as const;
  status?: number;
  details?: string;
  canRetry: boolean;
  shouldFallback = true;

  constructor(
    message: string,
    status?: number,
    details?: string,
    canRetry = true,
  ) {
    super(message);
    this.name = "GoogleMapsAPIError";
    this.status = status;
    this.details = details;
    this.canRetry = canRetry;
  }
}

export class GoogleMapsNetworkError extends Error implements GoogleMapsError {
  readonly type = "NETWORK_ERROR" as const;
  canRetry = true;
  shouldFallback = true;

  constructor(message = "Network error while connecting to Google Maps API") {
    super(message);
    this.name = "GoogleMapsNetworkError";
  }
}

export class GoogleMapsInvalidResponseError
  extends Error
  implements GoogleMapsError
{
  readonly type = "INVALID_RESPONSE" as const;
  canRetry = false;
  shouldFallback = true;

  constructor(
    message = "Invalid response from Google Maps API",
    public details?: string,
  ) {
    super(message);
    this.name = "GoogleMapsInvalidResponseError";
    this.details = details;
  }
}

type GoogleMapsErrorPayload = {
  error?: string;
  message?: string;
  details?: string;
};

function normalizeErrorPayload(data?: unknown): GoogleMapsErrorPayload {
  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;
    return {
      error: typeof record.error === "string" ? record.error : undefined,
      message: typeof record.message === "string" ? record.message : undefined,
      details: typeof record.details === "string" ? record.details : undefined,
    };
  }
  return {};
}

export function createGoogleMapsError(
  response: Response,
  responseData?: unknown,
): GoogleMapsError {
  const { status } = response;
  const payload = normalizeErrorPayload(responseData);

  // Handle empty or invalid responseData
  const errorMessage = payload.error || payload.message || "Unknown error";
  const errorDetails = payload.details || payload.error || undefined;

  switch (status) {
    case 503:
      if (
        errorMessage.includes("disabled") ||
        errorMessage.includes("Disabled")
      ) {
        return new GoogleMapsDisabledError(payload.message || errorMessage);
      }
      return new GoogleMapsAPIError(
        "Service unavailable",
        status,
        errorDetails,
      );

    case 401:
    case 403:
      return new GoogleMapsAPIError(
        "Unauthorized or forbidden",
        status,
        errorDetails,
        false,
      );

    case 429:
      return new GoogleMapsAPIError(
        "Rate limit exceeded",
        status,
        errorDetails,
        true,
      );

    case 404:
      return new GoogleMapsAPIError(
        "Resource not found",
        status,
        errorDetails,
        false,
      );

    case 400:
      return new GoogleMapsAPIError("Bad request", status, errorDetails, false);

    case 500:
    case 502:
    case 504:
      return new GoogleMapsAPIError("Server error", status, errorDetails, true);

    default:
      return new GoogleMapsAPIError(
        `HTTP ${status}: ${errorMessage}`,
        status,
        errorDetails,
      );
  }
}

export function isGoogleMapsError(error: unknown): error is GoogleMapsError {
  return (
    typeof error === "object" &&
    error !== null &&
    "type" in error &&
    "shouldFallback" in error
  );
}
